<template>
  <div class="gifts">
    <PageHeadlineWithLink
      title="利用済・期限切れのギフト"
      link-title="使えるギフト"
      @click-link="onClickHeadlineLink"
    />
    <div class="feedback">
      <transition name="fade" @enter="enterSuccessFeedback">
        <feedback-success
          v-if="showableSuccessFeedback"
          :message="successMessage"
        />
      </transition>
      <transition name="fade" @enter="enterErrorFeedback">
        <feedback-error v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div v-if="isLoadingTickets" class="gifts__loading">
      <loading />
    </div>
    <div v-else-if="!isEmptyResult" class="gift-contents__body">
      <div class="gift-contents__gift-boxes">
        <gift-card-ticket-box-used
          v-for="giftTicket in giftTickets"
          :key="giftTicket.id"
          :sender-icon="giftTicket.senderIcon"
          :sender-name="giftTicket.senderName"
          :url="giftTicket.url"
          :item-name="giftTicket.itemName"
          :item-sku-name="giftTicket.itemSkuName"
          :item-sku-image="giftTicket.itemSkuImage"
          :brand-name="giftTicket.brandName"
          :ticket-id="giftTicket.id"
          :is-personal-use="giftTicket.isPersonalUse"
          :received-at="giftTicket.receivedAt"
          :usable-limit-at="giftTicket.usableLimitAt"
          :revertable="!giftTicket.expired"
          @clicked-revert-button="showModal"
        />
      </div>
      <div class="gift-contents__pagination">
        <Pagination
          :current-page="currentPage"
          :total-pages="totalPages"
          @clicked="changePage"
        />
      </div>
      <TextModal
        v-if="showableModal && ticketId !== null"
        :title-text="'使えるギフトに戻す'"
        :caption-text="
          'このギフトを使った覚えがない方のみ\n動作を行ってください。'
        "
        :primary-button-text="'ギフトを戻す'"
        :cancel-button-text="'閉じる'"
        @clicked-modal-cover="closeModal"
        @clicked-primary="clickedPrimary"
        @clicked-cancel="closeModal"
      >
      </TextModal>
    </div>
    <div v-else class="gifts__empty-results">
      <p class="gifts__empty-result-message">
        利用済み・期限切れのギフトはここに表示されます
      </p>
    </div>
    <div class="gifts__to-giftee-top-area">
      <a href="/" class="gifts__to-giftee-top-link text-link-to-top">
        gifteeトップへ戻る
      </a>
    </div>
  </div>
</template>

<script>
import Pagination from '../../organism/paginations/Pagination'
import GiftCardTicketBoxUsed from '../../organism/gift_card_tickets/GiftCardTicketBoxUsed'
import Loading from '../../atoms/loadings/Loading'
import TextModal from '../../molecules/modals/text-modal'
import imageUrl from '../../../../../images/styleguide/none.png'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success'
import FeedbackError from '../../atoms/feedbacks/feedback-error'
import PageHeadlineWithLink from '../../molecules/headlines/PageHeadlineWithLink'

import axios from 'axios'
import url from 'url'
import qs from 'qs'

export default {
  components: {
    Pagination,
    GiftCardTicketBoxUsed,
    Loading,
    TextModal,
    FeedbackSuccess,
    FeedbackError,
    PageHeadlineWithLink
  },
  data() {
    return {
      giftTickets: [],
      isLoadingTickets: false,
      displayItemCount: 10,
      showableModal: false,
      ticketId: null,
      showableSuccessFeedback: false,
      successMessage: '',
      showableErrorFeedback: false,
      errorMessage: '',
      currentPage: 1,
      totalPages: 1
    }
  },
  computed: {
    isEmptyResult() {
      return this.giftTickets.length === 0
    }
  },
  async mounted() {
    this.initializeParams()
    await this.getUsedGifts(this.currentPage)

    // NOTE: ブラウザバック・フォワード時の更新処理
    window.onpopstate = async function() {
      this.initializeParams()
      await this.getUsedGifts(this.currentPage)
    }.bind(this)
  },
  methods: {
    initializeParams() {
      const urlQuery = url.parse(location.href).query
      if (!urlQuery) {
        this.currentPage = 1
        return
      }
      const parsed = qs.parse(urlQuery)
      this.currentPage = parsed.page ? Number(parsed.page) : 1
    },
    updateUrlFromParams() {
      history.pushState(
        '',
        '',
        this.currentPage <= 1
          ? '/mypage/used_gifts'
          : `/mypage/used_gifts?page=${this.currentPage}`
      )
    },
    async getUsedGifts(page) {
      this.isLoadingTickets = true

      try {
        const res = await axios.get(
          `/api/v1/users/@me/gifts?usable=false&page=${page || 1}&limit=${
            this.displayItemCount
          }`
        )
        if (res.data.data.length === 0) {
          this.isLoadingTickets = false
          return
        }
        const { current_page, total_pages } = res.data.meta
        this.currentPage = current_page
        this.totalPages = total_pages
        this.giftTickets = res.data.data.map(giftTicket => {
          const senderIcon =
            giftTicket.attributes.sender_icon !== null
              ? giftTicket.attributes.sender_icon
              : imageUrl

          // TODO: dummyimage を正式が画像に置換する
          const itemSkuImageUrl = giftTicket.attributes.item_sku_image
            ? giftTicket.attributes.item_sku_image
            : 'https://dummyimage.com/90x90/ccc/999.png&text=Not+Found'

          return {
            id: giftTicket.attributes.id,
            url: giftTicket.attributes.url,
            itemName: giftTicket.attributes.item_name,
            itemSkuImage: itemSkuImageUrl,
            itemSkuName: giftTicket.attributes.item_sku_name,
            brandName: giftTicket.attributes.brand_name,
            senderName: giftTicket.attributes.sender_name,
            senderIcon: senderIcon,
            isPersonalUse: giftTicket.attributes.personal_use,
            receivedAt: giftTicket.attributes.received_at,
            usableLimitAt: giftTicket.attributes.usable_limit_at,
            expired: giftTicket.attributes.expired
          }
        })
        this.isLoadingTickets = false
      } catch (error) {
        this.isLoadingTickets = false
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async clickedPrimary() {
      try {
        const res = await axios.delete(
          `/api/v1/users/@me/gifts/${this.ticketId}`
        )
        if (res.data.status === 200) {
          this.giftTickets = this.giftTickets.filter(
            giftTicket => giftTicket.id !== this.ticketId
          )
          this.ticketId = null
          this.closeModal()
          this.successMessage = '使えるギフトに戻しました'
          this.showableSuccessFeedback = true
        }
      } catch (error) {
        this.ticketId = null
        this.closeModal()
        this.showableErrorFeedback = true
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
      }
    },
    async changePage(page) {
      this.scrollToTop()
      await this.getUsedGifts(page)
      this.updateUrlFromParams()
    },
    scrollToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    },
    showModal(ticketId) {
      this.showableModal = true
      this.ticketId = ticketId
    },
    closeModal() {
      this.showableModal = false
      this.ticketId = null
    },
    enterSuccessFeedback() {
      setTimeout(() => {
        this.showableSuccessFeedback = false
      }, 3000)
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    onClickHeadlineLink() {
      location.href = '/mypage/gifts'
    }
  }
}
</script>
